/**
 * client_match_controller.js — offer existing clients while a new client is typed (Stimulus).
 *
 * Wraps a new-client form section: the quote and job forms' new-client fields
 * (components/forms/_client_site_fields.html) and the client form. As the
 * name, phone, email or address is filled in, it asks /clients/match which
 * existing clients those details may already be, and lists them in a panel
 * under the fields with the reason each matched ("Same name and phone").
 *
 * Every match is a suggestion: a household can share a number and two John
 * Smiths are ordinary, so the user may always carry on creating the new
 * client. The rule for what matches lives server-side in
 * application/utils/client_match_rules.py; this controller only shows it.
 *
 * Each candidate offers one action. Inside a form with a client picker (quote,
 * job) it is "Use this client", which switches the form to that existing
 * client the same way a user would: tick the "existing" radio, choose the
 * client, and fire change on both so the site and contact pickers follow.
 * Without a picker (the client form) it is a link to the client's page.
 * "Different person" hides that candidate until the page reloads.
 *
 * Markup contract:
 *   <div data-controller="client-match"
 *        data-client-match-url-value="/clients/match"
 *        data-action="input->client-match#schedule change->client-match#schedule">
 *     name / phone / email / address inputs (by name attribute)
 *     <div data-client-match-target="panel" hidden></div>
 *   </div>
 */
import { Controller } from "@hotwired/stimulus";

const LOOKUP_DELAY_MS = 400;
const MIN_PHONE_DIGITS = 8;
const MIN_ADDRESS_LENGTH = 6;
const COMPLETE_EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export default class extends Controller {
    static targets = ["panel"];
    static values = { url: String };

    connect() {
        this._dismissed = new Set();
    }

    disconnect() {
        clearTimeout(this._timer);
        this._request?.abort();
    }

    /** Look up the typed details once typing pauses. */
    schedule() {
        clearTimeout(this._timer);
        this._timer = setTimeout(() => this.check(), LOOKUP_DELAY_MS);
    }

    async check() {
        this._request?.abort();
        const details = this.lookupDetails();
        if (!details) return this.clear();

        this._request = new AbortController();
        try {
            const response = await fetch(`${this.urlValue}?${details}`, {
                credentials: 'same-origin',
                headers: { Accept: 'application/json' },
                signal: this._request.signal,
            });
            if (!response.ok) return this.clear();
            const { candidates } = await response.json();
            this.show(candidates.filter((c) => !this._dismissed.has(c.id)));
        } catch (error) {
            if (error.name !== 'AbortError') this.clear();
        }
    }

    /** Query string of the details worth asking about, or null when none is. */
    lookupDetails() {
        const params = new URLSearchParams();
        const name = this.typedName();
        const phone = this.field('phone');
        const email = this.field('email');
        const address = this.field('address_input');
        if (name) params.set('name', name);
        if (phone.replace(/\D/g, '').length >= MIN_PHONE_DIGITS) params.set('phone', phone);
        if (COMPLETE_EMAIL.test(email)) params.set('email', email);
        if (address.length >= MIN_ADDRESS_LENGTH) params.set('address', address);
        return params.toString() || null;
    }

    /** The business name for a business client, otherwise first + last once both are typed. */
    typedName() {
        const type = this.element.querySelector('input[name="client_type"]:checked')?.value;
        if (type === 'business') return this.field('business_name');
        const first = this.field('first_name');
        const last = this.field('last_name');
        return first && last ? `${first} ${last}` : '';
    }

    field(name) {
        return this.element.querySelector(`[name="${name}"]`)?.value.trim() || '';
    }

    show(candidates) {
        if (!candidates.length) return this.clear();
        const strong = candidates.some((c) => c.strong);
        this.panelTarget.className = `alert ${strong ? 'alert-warning' : 'alert-info'} mt-3 mb-0`;
        this.panelTarget.replaceChildren(
            this.heading(strong),
            ...candidates.map((candidate) => this.row(candidate)),
        );
        this.panelTarget.hidden = false;
    }

    heading(strong) {
        const heading = document.createElement('p');
        heading.className = 'fw-semibold mb-2';
        const icon = document.createElement('i');
        icon.className = `bi ${strong ? 'bi-exclamation-triangle' : 'bi-info-circle'} me-2`;
        heading.append(icon, strong
            ? 'This looks like a client you already have'
            : 'You may already have this client');
        return heading;
    }

    row(candidate) {
        const row = document.createElement('div');
        row.className = 'd-flex flex-wrap align-items-center gap-2 py-2 border-top';

        const text = document.createElement('div');
        text.className = 'flex-grow-1';
        const name = document.createElement('strong');
        name.textContent = candidate.name;
        const reason = document.createElement('span');
        reason.className = 'ms-2 badge text-bg-light';
        reason.textContent = candidate.reason;
        text.append(name, reason);
        if (candidate.detail) text.append(this.line(candidate.detail, 'small text-body-secondary'));
        if (candidate.blocks_xero_sync) {
            text.append(this.line(
                `Xero already has a contact called ${candidate.name}, so a new client with this name won't sync to Xero.`,
                'small fw-semibold mt-1',
            ));
        }

        row.append(text, this.useAction(candidate), this.dismissButton(candidate));
        return row;
    }

    line(content, className) {
        const line = document.createElement('div');
        line.className = className;
        line.textContent = content;
        return line;
    }

    /** "Use this client" when the form can switch to it, otherwise a link to the client. */
    useAction(candidate) {
        if (this.pickerOption(candidate.id)) {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'btn btn-primary btn-sm';
            button.textContent = 'Use this client';
            button.dataset.action = 'client-match#use';
            button.dataset.clientMatchIdParam = String(candidate.id);
            return button;
        }
        const link = document.createElement('a');
        link.className = 'btn btn-outline-primary btn-sm';
        link.href = candidate.url;
        link.dataset.turboFrame = '_top';
        link.textContent = 'Open client';
        return link;
    }

    dismissButton(candidate) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'btn btn-link btn-sm text-body-secondary';
        button.textContent = 'Different person';
        button.dataset.action = 'client-match#dismiss';
        button.dataset.clientMatchIdParam = String(candidate.id);
        return button;
    }

    use({ params: { id } }) {
        const form = this.element.closest('form');
        const existing = form?.querySelector('input[name="client_mode"][value="existing"]');
        const select = form?.querySelector('select[name="client_id"]');
        if (!existing || !select) return;

        existing.checked = true;
        existing.dispatchEvent(new Event('change'));
        if (select.choicesInstance) {
            select.choicesInstance.setChoiceByValue(String(id));
        } else {
            select.value = String(id);
        }
        select.dispatchEvent(new Event('change'));
        this.clear();
        select.closest('[data-client-mode-target="existingFields"]')
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    dismiss({ params: { id } }) {
        this._dismissed.add(id);
        this.check();
    }

    clear() {
        this.panelTarget.replaceChildren();
        this.panelTarget.hidden = true;
    }

    pickerOption(clientId) {
        const select = this.element.closest('form')?.querySelector('select[name="client_id"]');
        return select?.querySelector(`option[value="${clientId}"]`) || null;
    }
}
